import { createFeatureSelector, createSelector } from '@ngrx/store'
import { IBarterState } from './barter.reducer'
import { StoreKeysEnum } from '../../../store/store-keys'
import { ICalculateBarterResultParcelsDate } from '../interfaces/calculate-barter-result-data.interface'
import { selectLpisKulturas } from './lpis-kultura.selectors'

// For select TOP level store feature
export const selectBarterFeature = createFeatureSelector<Readonly<IBarterState>>(StoreKeysEnum.BarterFeature)

export const selectBarter = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.barter,
)

export const selectBarterResult = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.barterResult,
)

export const selectBarterSelectedTab = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.selectedTab,
)

export const selectBarterConcernedParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.concernedParcels,
)

export const selectBarterConcernedParcelAcreages = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.concernedParcelAcreages,
)
export const selectBarterConcernedParcelAcreagesSum = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.concernedParcelAcreagesSum,
)
export const selectBarterConcernedParcelGroupsOwnerships = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.concernedParcelGroupsOwnerships,
)

export const selectBarterCounterpartyParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.counterpartyParcels,
)

export const selectBarterCounterpartyParcelAcreages = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.counterpartyParcelAcreages,
)

export const selectBarterCounterpartyParcelAcreagesSum = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.counterpartyParcelAcreagesSum,
)
export const selectBarterCounterpartyParcelGroupsOwnerships = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.counterpartyParcelGroupsOwnerships,
)

export const selectConcernedParcelIds = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.concernedParcelIds,
)

export const selectCounterpartyParcelIds = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.counterpartyParcelIds,
)

export const selectPassedParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcels,
)
export const selectIsAnyPassedParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState): boolean => state.passedParcels?.length > 0,
)
export const selectPassedParcelAcreages = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelAcreages,
)
export const selectPassedParcelAcreagesSum = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelAcreagesSum,
)
export const selectPassedParcelGeometries = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelGeometries,
)
export const selectPassedParcelGroupsOwnerships = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelGroupsOwnerships,
)

export const selectPassedParcelIds = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelIds,
)

export const selectReceivedParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcels,
)
export const selectIsAnyReceivedParcels = createSelector(
    selectBarterFeature,
    (state: IBarterState): boolean => state.receivedParcels?.length > 0,
)
export const selectReceivedParcelAcreages = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcelAcreages,
)
export const selectReceivedParcelAcreagesSum = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcelAcreagesSum,
)

export const selectReceivedParcelIds = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcelIds,
)
export const selectBarterReceivedParcelGeometries = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcelGeometries,
)
export const selectBarterPassedReceivedParcelsMapMode = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedReceivedParcelsMapMode,
)

export const selectBarterReloadMapParcels = createSelector( // read on barter-show
    selectBarterFeature,
    (state: IBarterState) => state.reloadMapParcels,
)

export const selectBarterAllParcelIds = createSelector( // read on barter-show
    selectConcernedParcelIds,
    selectCounterpartyParcelIds,
    selectPassedParcelIds,
    selectReceivedParcelIds,
    (concernedParcelIds, counterpartyParcelIds, passedParcelIds, receivedParcelIds) => [].concat(concernedParcelIds, counterpartyParcelIds, passedParcelIds, receivedParcelIds),
)

export const selectIsAnyConcernedOrCounterpartyParcelUpdated = createSelector( // read on barter-show
    selectBarterFeature,
    (state: IBarterState) => state.isAnyConcernedOrCounterpartyParcelUpdated,
)

export const selectAnyPassedOrReceivedParcel = createSelector( // read on barter-show
    selectIsAnyPassedParcels,
    selectIsAnyReceivedParcels,
    (anyPassedParcels, anyReceivedParcels): boolean => anyPassedParcels || anyReceivedParcels,
)

export const selectBarterAnyParcel = createSelector( // read on barter-show
    selectPassedParcels,
    selectReceivedParcels,
    selectBarterConcernedParcels,
    selectBarterCounterpartyParcels,
    (passedParcels, receivedParcels, concernedParcels, counterpartyParcels): boolean => (passedParcels?.length + receivedParcels?.length + concernedParcels?.length + counterpartyParcels?.length) > 0,
)

export const selectIncomingPayments = createSelector( // read on barter-show
    selectBarterFeature,
    (state: IBarterState) => state.incomingPayments,
)

export const selectOutgoingPayments = createSelector( // read on barter-show
    selectBarterFeature,
    (state: IBarterState) => state.outgoingPayments,
)

export const selectBarterBarterId = createSelector(
    selectBarter,
    (barter) => barter?.id,
)
export const selectBarterBarterGroupId = createSelector(
    selectBarter,
    (barter) => barter.groupId,
)
export const selectBarterState = createSelector(
    selectBarter,
    (barter) => barter.state,
)
export const selectBarterCounterparty = createSelector(
    selectBarter,
    (barter) => barter.counterparty,
)
export const selectBarterCounterpartyId = createSelector(
    selectBarter,
    (barter) => barter.counterpartyId,
)
export const selectBarterBarterCounterpartyId = createSelector(
    selectBarter,
    (barter) => barter.counterparty?.id,
)
export const selectBarterConcernedId = createSelector(
    selectBarter,
    (barter) => barter.concernedId,
)
export const selectBarterConcernedSubject = createSelector(
    selectBarter,
    (barter) => barter.concernedSubject,
)

export const selectBarterVersionType = createSelector(
    selectBarter,
    (barter) => barter.versionType,
)

export const selectBarterToEconomicYear = createSelector(
    selectBarter,
    (barter) => barter.toEconomicYear,
)

export const selectBarterIsEditingBarter = createSelector(
    selectBarterFeature,
    (feature) => feature?.isEditingBarter,
)

export const selectBarterNote = createSelector(
    selectBarter,
    (barter) => barter.note,
)

export const selectBarterIndefinite = createSelector(
    selectBarter,
    (barter) => barter.indefinite,
)
export const selectBarterReusedPreviousExpiration = createSelector(
    selectBarter,
    (barter) => barter.reusedPreviousExpiration,
)
export const selectBarterDurationMonths = createSelector(
    selectBarter,
    (barter) => barter.durationMonths,
)
export const selectBarterDurationMonthsUnit = createSelector(
    selectBarter,
    (barter) => barter.durationMonthsUnit,
)
export const selectBarterDurationToDate = createSelector(
    selectBarter,
    (barter) => barter.durationToDate,
)
export const selectBarterNoticePeriodMonths = createSelector(
    selectBarter,
    (barter) => barter.noticePeriodMonths,
)
export const selectBarterNoticePeriodMonthsUnit = createSelector(
    selectBarter,
    (barter) => barter.noticePeriodMonthsUnit,
)
export const selectBarterEndType = createSelector(
    selectBarter,
    (barter) => barter.endType,
)
export const selectBarterEndDate = createSelector(
    selectBarter,
    (barter) => barter.endDate,
)
export const selectBarterResignDate = createSelector(
    selectBarter,
    (barter) => barter.resignDate,
)
export const selectBarterExpirationDate = createSelector(
    selectBarter,
    (barter) => barter.expirationDate,
)
export const selectBarterPaymentsFromEconomicYear = createSelector( // read on ca-contract-payment-year-type
    selectBarter,
    (barter) => barter.paymentsFromEconomicYear,
)

export const selectBarterEfficiencyFromDate = createSelector( // read on ca-signature-date
    selectBarter,
    (state) => state.efficiencyFromDate,
)

export const selectBarterParcelOrder = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.parcelOrder,
)

export const selectBarterParcelConflicts = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.parcelConflicts,
)

export const selectBarterPassedParcelGeometries = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.passedParcelGeometries,
)

export const selectReceivedParcelGroupsOwnerships = createSelector(
    selectBarterFeature,
    (state: IBarterState) => state.receivedParcelGroupsOwnerships,
)

// ==> barter.effects
export const selectBarterParcelOrderAndConflicts = createSelector(
    selectBarterParcelOrder,
    selectBarterParcelConflicts,
    (parcelOrder, parcelConflicts) => ({ parcelOrder, parcelConflicts }),
)
export const selectBarterParcelOrderAncConflictsAndPassedParcelGeometries = createSelector(
    selectBarterParcelOrder,
    selectBarterParcelConflicts,
    selectBarterPassedParcelGeometries,
    (parcelOrder, parcelConflicts, passedParcelGeometries) => ({ parcelOrder, parcelConflicts, passedParcelGeometries }),
)
export const selectBarterParcelOrderAncConflictsAndReceivedParcelGeometries = createSelector(
    selectBarterParcelOrder,
    selectBarterParcelConflicts,
    selectBarterReceivedParcelGeometries,
    (parcelOrder, parcelConflicts, receivedParcelGeometries) => ({ parcelOrder, parcelConflicts, receivedParcelGeometries }),
)
export const selectBarterCalculateResultData = createSelector(
    selectBarterFeature,
    selectLpisKulturas,
    (state: IBarterState, lpisKulturasState): ICalculateBarterResultParcelsDate => ({
        stateConcernedParcelIds: state.concernedParcelIds,
        stateConcernedParcelAcreages: state.concernedParcelAcreages,
        stateCounterpartyParcelIds: state.counterpartyParcelIds,
        stateCounterpartyParcelAcreages: state.counterpartyParcelAcreages,
        statePassedParcelIds: state.passedParcelIds,
        statePassedParcelAcreages: state.passedParcelAcreages,
        stateReceivedParcelIds: state.receivedParcelIds,
        stateReceivedParcelAcreages: state.receivedParcelAcreages,
        lpisKulturasState
    })
)
export const selectAddBarterConcernedParcelsData = createSelector(
    selectBarterBarterId,
    selectBarterCounterpartyId,
    selectBarterConcernedParcels,
    selectBarterConcernedParcelAcreages,
    selectBarterConcernedSubject,
    selectBarterCounterparty,
    selectBarterParcelConflicts,
    (barterId, barterCounterpartyId, concernedParcels, concernedParcelAcreages, concernedSubject, counterparty, parcelConflicts) => ({
        barterId,
        barterCounterpartyId,
        concernedParcels,
        concernedParcelAcreages,
        concernedSubject,
        counterparty,
        parcelConflicts,
    }),
)
export const selectAddBarterCounterpartyParcelsData = createSelector(
    selectBarterBarterId,
    selectBarterCounterpartyParcels,
    selectBarterCounterpartyParcelAcreages,
    selectBarterConcernedSubject,
    selectBarterCounterparty,
    selectBarterParcelConflicts,
    (barterId, counterpartyParcels, counterpartyParcelAcreages, concernedSubject, counterparty, parcelConflicts) => ({
        barterId,
        counterpartyParcels,
        counterpartyParcelAcreages,
        concernedSubject,
        counterparty,
        parcelConflicts,
    }),
)
export const selectBarterConcernedParcelsData = createSelector(
    selectBarterConcernedParcels,
    selectBarterConcernedParcelAcreages,
    selectBarterParcelOrder,
    (concernedParcels, concernedParcelAcreages, parcelOrder) => ({ concernedParcels, concernedParcelAcreages, parcelOrder }),
)
export const selectBarterCounterpartyParcelsData = createSelector(
    selectBarterCounterpartyParcels,
    selectBarterCounterpartyParcelAcreages,
    selectBarterParcelOrder,
    (counterpartyParcels, counterpartyParcelAcreages, parcelOrder) => ({ counterpartyParcels, counterpartyParcelAcreages, parcelOrder }),
)
export const selectBarterPassedParcelsData = createSelector(
    selectPassedParcels,
    selectPassedParcelAcreages,
    selectBarterParcelOrder,
    (passedParcels, passedParcelAcreages, parcelOrder) => ({ passedParcels, passedParcelAcreages, parcelOrder }),
)
export const selectBarterReceivedParcelsData = createSelector(
    selectReceivedParcels,
    selectReceivedParcelAcreages,
    selectBarterParcelOrder,
    (receivedParcels, receivedParcelAcreages, parcelOrder) => ({ receivedParcels, receivedParcelAcreages, parcelOrder }),
)
export const selectBarterUpdateAcreageUsedAcreage = createSelector(
    selectBarterBarterGroupId,
    selectBarterConcernedParcels,
    selectBarterConcernedParcelAcreages,
    selectBarterCounterpartyParcels,
    selectBarterCounterpartyParcelAcreages,
    selectBarterConcernedSubject,
    selectBarterCounterparty,
    selectBarterParcelConflicts,
    (barterGroupId, concernedParcels, concernedParcelAcreages, counterpartyParcels, counterpartyParcelAcreages, concernedSubject, counterparty, parcelConflicts) => ({
        barterGroupId,
        concernedParcels,
        concernedParcelAcreages,
        counterpartyParcels,
        counterpartyParcelAcreages,
        concernedSubject,
        counterparty,
        parcelConflicts,
    }),
)
// ==> ca-barter-create
export const selectBarterCreateBarterState = createSelector(
    selectBarterSelectedTab,
    selectBarterPassedReceivedParcelsMapMode,
    selectBarterReloadMapParcels,
    selectBarterAllParcelIds,
    selectBarterIsEditingBarter,
    (selectedTab, passedReceivedParcelsMapMode, reloadMapParcels, allParcelsIDs, isEditingBarter) => ({ selectedTab, passedReceivedParcelsMapMode, reloadMapParcels, allParcelsIDs, isEditingBarter }),
)
export const selectBarterConcernedParcelsState = createSelector(
    selectBarterConcernedParcels,
    selectConcernedParcelIds,
    selectBarterConcernedParcelAcreages,
    (concernedParcels, concernedParcelIds, concernedParcelAcreages) => ({
        concernedParcels,
        concernedParcelIds,
        concernedParcelAcreages,
    }),
)
export const selectBarterCounterpartyParcelsState = createSelector(
    selectBarterCounterpartyParcels,
    selectCounterpartyParcelIds,
    selectBarterCounterpartyParcelAcreages,
    (counterpartyParcels, counterpartyParcelIds, counterpartyParcelAcreages) => ({
        counterpartyParcels,
        counterpartyParcelIds,
        counterpartyParcelAcreages,
    }),
)
export const selectBarterPassedParcelsState = createSelector(
    selectPassedParcels,
    selectPassedParcelAcreages,
    selectPassedParcelGeometries,
    (passedParcels, passedParcelAcreages, passedParcelGeometries) => ({ passedParcels, passedParcelAcreages, passedParcelGeometries }),
)
export const selectBarterReceivedParcelsState = createSelector(
    selectReceivedParcels,
    selectReceivedParcelAcreages,
    selectBarterReceivedParcelGeometries,
    (receivedParcels, receivedParcelAcreages, receivedParcelGeometries) => ({
        receivedParcels,
        receivedParcelAcreages,
        receivedParcelGeometries,
    }),
)
// ==> Barter-show
export const selectBarterShowState = createSelector(
    selectBarterConcernedParcels,
    selectConcernedParcelIds,
    selectBarterCounterpartyParcels,
    selectCounterpartyParcelIds,
    selectPassedParcels,
    selectPassedParcelIds,
    selectReceivedParcels,
    selectReceivedParcelIds,
    selectIncomingPayments,
    selectOutgoingPayments,
    selectBarterSelectedTab,
    (concernedParcels, concernedParcelIds, counterpartyParcels, counterpartyParcelIds, passedParcels, passedParcelIds, receivedParcels, receivedParcelIds, incomingPayments, outgoingPayments, selectedTab) => ({
        concernedParcels,
        concernedParcelIds,
        counterpartyParcels,
        counterpartyParcelIds,
        passedParcels,
        passedParcelIds,
        receivedParcels,
        receivedParcelIds,
        incomingPayments,
        outgoingPayments,
        selectedTab,
    }),
)
// ==> ca-barter-parcel-list
export const selectBarterParcelListState = createSelector(
    selectBarter,
    selectBarterBarterId,
    selectBarterBarterGroupId,
    selectBarterConcernedSubject,
    selectIsAnyConcernedOrCounterpartyParcelUpdated,
    selectBarterParcelOrder,
    selectBarterBarterCounterpartyId,
    selectAnyPassedOrReceivedParcel,
    (barter, barterId, barterGroupId, concernedSubject, isAnyConcernedOrCounterpartyParcelUpdated, parcelOrder, counterPartyId, anyPassedOrReceivedParcel) => ({
        barter,
        barterId,
        barterGroupId,
        concernedSubject,
        isAnyConcernedOrCounterpartyParcelUpdated,
        parcelOrder,
        counterPartyId,
        anyPassedOrReceivedParcel,
    }),
)
export const selectBarterParcelListConcernedState = createSelector(
    selectBarterConcernedParcels,
    selectBarterConcernedParcelAcreages,
    selectBarterConcernedParcelAcreagesSum,
    selectBarterConcernedParcelGroupsOwnerships,
    selectBarterCounterparty,
    (concernedParcels, concernedParcelAcreages, concernedParcelAcreagesSum, concernedParcelGroupsOwnerships, counterparty) => ({
        concernedParcels,
        concernedParcelAcreages,
        concernedParcelAcreagesSum,
        concernedParcelGroupsOwnerships,
        counterparty,
    }),
)
export const selectBarterParcelListCounterpartyState = createSelector(
    selectBarterCounterpartyParcels,
    selectBarterCounterpartyParcelAcreages,
    selectBarterConcernedSubject,
    selectBarterCounterpartyParcelAcreagesSum,
    selectBarterCounterpartyParcelGroupsOwnerships,
    (counterpartyParcels, counterpartyParcelAcreages, concernedSubject, counterpartyParcelAcreagesSum, counterpartyParcelGroupsOwnerships) => ({
        counterpartyParcels,
        counterpartyParcelAcreages,
        concernedSubject,
        counterpartyParcelAcreagesSum,
        counterpartyParcelGroupsOwnerships,
    }),
)
export const selectBarterParcelListPassedState = createSelector(
    selectPassedParcels,
    selectPassedParcelAcreages,
    selectPassedParcelAcreagesSum,
    selectPassedParcelGeometries,
    selectPassedParcelGroupsOwnerships,
    selectBarterConcernedSubject,
    (passedParcels, passedParcelAcreages, passedParcelAcreagesSum, passedParcelGeometries, passedParcelGroupsOwnerships, concernedSubject) => ({
        passedParcels,
        passedParcelAcreages,
        passedParcelAcreagesSum,
        passedParcelGeometries,
        passedParcelGroupsOwnerships,
        concernedSubject,
    }),
)
export const selectBarterParcelListReceivedState = createSelector(
    selectReceivedParcels,
    selectReceivedParcelAcreages,
    selectReceivedParcelAcreagesSum,
    selectBarterReceivedParcelGeometries,
    selectReceivedParcelGroupsOwnerships,
    selectBarterCounterparty,
    (receivedParcels, receivedParcelAcreages, receivedParcelAcreagesSum, receivedParcelGeometries, receivedParcelGroupsOwnerships, counterparty) => ({
        receivedParcels,
        receivedParcelAcreages,
        receivedParcelAcreagesSum,
        receivedParcelGeometries,
        receivedParcelGroupsOwnerships,
        counterparty,
    }),
)
// ==> ca-barter-result
export const selectBarterResultBarterState = createSelector(
    selectBarterBarterId,
    selectBarterState,
    selectBarterCounterparty,
    selectBarterConcernedSubject,
    selectAnyPassedOrReceivedParcel,
    selectBarterAnyParcel,
    selectBarterResult,
    selectIsAnyConcernedOrCounterpartyParcelUpdated,
    (barterId, barterState, barterCounterparty, barterConcernedSubject, anyPassedOrReceivedParcel, anyParcel, barterResult, isAnyConcernedOrCounterpartyParcelUpdated) => ({
        barterId,
        barterState,
        barterCounterparty,
        barterConcernedSubject,
        anyPassedOrReceivedParcel,
        anyParcel,
        barterResult,
        isAnyConcernedOrCounterpartyParcelUpdated,
    }),
)
// ==> ca-note-show
export const selectBarterNoteState = createSelector(
    selectBarterBarterId,
    selectBarterNote,
    (objectId, note) => ({ objectId, note }),
)
// ==> ca-duration-period
export const selectBarterDurationPeriodState = createSelector(
    selectBarterIndefinite,
    selectBarterReusedPreviousExpiration,
    selectBarterDurationMonths,
    selectBarterDurationMonthsUnit,
    selectBarterDurationToDate,
    (indefinite, reusedPreviousExpiration, durationMonths, durationMonthsUnit, durationToDate) => ({
        indefinite,
        reusedPreviousExpiration,
        durationMonths,
        durationMonthsUnit,
        durationToDate,
    }),
)
// ==> ca-barter-pass-receive-parcels-map
export const selectBarterPassReceiveParcelsMap = createSelector(
    selectIsAnyPassedParcels,
    selectIsAnyReceivedParcels,
    selectBarterPassedReceivedParcelsMapMode,
    selectBarterCounterpartyParcels,
    selectBarterConcernedParcels,
    (isAnyPassedParcel, isAnyReceivedParcel, passedReceivedParcelsMapMode, counterpartyParcels, concernedParcels) => ({ isAnyPassedParcel, isAnyReceivedParcel, passedReceivedParcelsMapMode, counterpartyParcels, concernedParcels }),
)
// ==> ca-end-dialog
export const selectBarterEndDialogState = createSelector(
    selectBarter,
    selectBarterEfficiencyFromDate,
    selectBarterEndType,
    selectBarterEndDate,
    selectBarterResignDate,
    selectBarterExpirationDate,
    (object, efficiencyFromDate, endType, endDate, resignDate, expirationDate) => ({ object, efficiencyFromDate, endType, endDate, resignDate, expirationDate }),
)
// ==> ca-outgoing-payments-list
export const selectOutgoingPaymentsListBarterState = createSelector(
    selectBarterBarterId,
    selectBarterState,
    selectBarterCounterparty,
    selectOutgoingPayments,
    (barterId, barterState, barterCounterparty, outgoingPayments) => ({ barterId, barterState, barterCounterparty, outgoingPayments }),
)
// ==> ca-notice-period
export const selectBarterNoticePeriod = createSelector(
    selectBarterNoticePeriodMonths,
    selectBarterNoticePeriodMonthsUnit,
    selectBarterIndefinite,
    (noticePeriodMonths, noticePeriodMonthsUnit, indefinite) => ({ noticePeriodMonths, noticePeriodMonthsUnit, indefinite }),
)
// ==> ca-barter-pass-receive-result-bar
export const selectBarterOppositeArea = createSelector(
    selectBarterPassedReceivedParcelsMapMode,
    selectReceivedParcelAcreagesSum,
    selectPassedParcelAcreagesSum,
    (mode, receivedParcelAcreagesSum, passedParcelAcreagesSum) => mode === 'pass' ? receivedParcelAcreagesSum : passedParcelAcreagesSum
)